import { Injectable, TemplateRef, ViewContainerRef } from '@angular/core';
import { BehaviorSubject, delay, Observable, tap } from 'rxjs';
import {
  ComponentPortal,
  ComponentType,
  TemplatePortal,
} from '@angular/cdk/portal';
import { assign } from 'lodash';

interface BottomBarConfig {
  position: 'sticky' | 'fixed';
}

enum Status {
  Hidden = 'hidden',
  Visible = 'visible',
}

@Injectable({
  providedIn: 'root',
})
export class BottomBarService {
  readonly portal$!: Observable<
    TemplatePortal<unknown> | ComponentPortal<unknown> | null
  >;
  private readonly _portal$!: BehaviorSubject<
    TemplatePortal<unknown> | ComponentPortal<unknown> | null
  >;
  readonly state$!: Observable<string>;
  readonly config: BottomBarConfig = {
    position: 'sticky',
  };
  private readonly _state$!: BehaviorSubject<Status>;
  opened!: boolean;

  constructor() {
    this._portal$ = new BehaviorSubject(null) as BehaviorSubject<any>;
    this.portal$ = this._portal$.asObservable().pipe(
      delay(0),
      tap((portal) => {
        this.opened = !!portal;
      })
    );

    this._state$ = new BehaviorSubject<Status>(Status.Visible);
    this.state$ = this._state$.asObservable();
  }

  open(
    templateRef: TemplateRef<unknown>,
    viewContainerRef: ViewContainerRef,
    configOverride?: Partial<BottomBarConfig>
  ): void;
  open(
    component: ComponentType<unknown>,
    viewContainerRef?: ViewContainerRef,
    configOverride?: Partial<BottomBarConfig>
  ): void;
  open(
    item: TemplateRef<unknown> | ComponentType<unknown>,
    viewContainerRef: ViewContainerRef,
    configOverride?: Partial<BottomBarConfig>
  ): void {
    const template: TemplatePortal | ComponentPortal<unknown> =
      item instanceof TemplateRef
        ? new TemplatePortal(item, viewContainerRef)
        : new ComponentPortal(item, viewContainerRef);

    assign(this.config, configOverride);
    this._portal$.next(template);
  }

  close(): void {
    this._portal$.next(null);
  }

  hide(): void {
    this.updateStatus(Status.Hidden);
  }

  show(): void {
    this.updateStatus(Status.Visible);
  }

  private updateStatus(status: Status): void {
    this._state$.next(status);
  }
}
